import { message } from "antd";
import { makeAutoObservable, runInAction } from "mobx";
import { B2BError, b2bErrorCode, b2bErrorText, onB2BError } from "@/utils/b2bErrors";
// FR-006: корп-админы компании (UC1116). Backs AdminsTab. Пароль в сторе не хранится.
export class CorpAdminsStore {
    constructor(corpAdminService) {
        this.corpAdminService = corpAdminService;
        this.companyId = null;
        this.admins = [];
        this.isLoading = false;
        this.isSubmitting = false;
        this.createError = null;
        this.generation = 0;
        makeAutoObservable(this, { generation: false });
    }
    init(companyId) {
        this.dispose();
        this.companyId = companyId;
        void this.load();
    }
    clearCreateError() {
        this.createError = null;
    }
    async load() {
        const companyId = this.companyId;
        if (!companyId)
            return;
        const generation = this.generation;
        this.isLoading = true;
        try {
            const admins = await this.corpAdminService.getCompanyAdmins(companyId);
            runInAction(() => {
                if (generation === this.generation)
                    this.admins = admins;
            });
        }
        catch (error) {
            onB2BError(error, "Не удалось загрузить администраторов компании");
        }
        finally {
            runInAction(() => {
                if (generation === this.generation)
                    this.isLoading = false;
            });
        }
    }
    /** Создание через edge `corp-admin-manage` (§4.5); ошибка — у формы (`createError`). */
    async create(input) {
        const companyId = this.companyId;
        if (!companyId)
            return false;
        this.isSubmitting = true;
        this.createError = null;
        try {
            await this.corpAdminService.createAdmin({ ...input, companyIds: [companyId] });
            message.success("Администратор создан. Передайте ему email и пароль для входа");
            await this.load();
            return true;
        }
        catch (error) {
            runInAction(() => {
                this.createError = {
                    code: b2bErrorCode(error),
                    text: b2bErrorText(error, "Не удалось создать администратора — повторите попытку"),
                };
            });
            return false;
        }
        finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }
    async run(action, successText, failureText) {
        this.isSubmitting = true;
        try {
            await action();
            message.success(successText);
            await this.load();
            return true;
        }
        catch (error) {
            onB2BError(error, failureText);
            return false;
        }
        finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }
    block(adminId, reason) {
        return this.run(() => this.corpAdminService.blockAdmin(adminId, reason), "Администратор заблокирован", "Не удалось заблокировать администратора");
    }
    unblock(adminId) {
        return this.run(() => this.corpAdminService.unblockAdmin(adminId), "Администратор разблокирован", "Не удалось разблокировать администратора");
    }
    setPassword(adminId, password) {
        return this.run(() => this.corpAdminService.setPassword(adminId, password), "Пароль изменён. Передайте новый пароль администратору", "Не удалось изменить пароль");
    }
    /**
     * «Открепить от компании» — `corp_admin_set_companies` без этой компании (BR-ACC-26).
     * Набор компаний перечитывается перед записью: RPC приводит набор к переданному, и снимок из
     * списка мог устареть (другой оператор мог закрепить админа ещё за одной компанией).
     */
    detach(admin) {
        const companyId = this.companyId;
        if (!companyId)
            return Promise.resolve(false);
        return this.run(async () => {
            // Перечитываем набор прямой выборкой (не через load(), который глотает свои ошибки): если чтение
            // не удалось или админа уже открепил другой оператор — ничего не пишем, иначе вернули бы ему снятые компании.
            const admins = await this.corpAdminService.getCompanyAdmins(companyId);
            runInAction(() => {
                this.admins = admins;
            });
            const fresh = admins.find((row) => row.adminId === admin.adminId);
            if (!fresh)
                throw new B2BError(null, "NOT_FOUND");
            return this.corpAdminService.setCompanies(fresh.adminId, fresh.companyIds.filter((id) => id !== companyId));
        }, "Администратор откреплён от компании", "Не удалось открепить администратора");
    }
    dispose() {
        this.generation += 1;
        this.companyId = null;
        this.admins = [];
        this.isLoading = false;
        this.isSubmitting = false;
        this.createError = null;
    }
}
